/**
 * UnifiedProviderCard — single component for editing a provider everywhere:
 * setup wizard, admin settings, CSV import review.
 *
 * Two modes:
 * - Collapsed: compact multi-row spreadsheet grid showing all key fields.
 * - Expanded: all seven sections shown flat (no per-section accordions).
 *   A Save button at the bottom saves and collapses.
 *
 * Conflicts (website crawl vs DB) show inline with Keep Current / Use This / Edit.
 */
import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import LanguageIcon from '@mui/icons-material/Language';
import StorageIcon from '@mui/icons-material/Storage';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { ProviderInfo, CrawlConflict } from '../../types';
import type { DataSource, RoleOption, SelectOption } from './providerCardHelpers';
import { SECTION_LABELS } from './providerCardHelpers';
import {
  IdentitySection,
  ClinicalSection,
  BioSection,
  SchedulingSection,
  BillingSection,
  TelehealthSection,
  AddressSection,
} from './ProviderSections';
import type { SectionProps } from './ProviderSections';

export type { DataSource } from './providerCardHelpers';
export type { RoleOption, SelectOption } from './providerCardHelpers';

export interface UnifiedProviderCardProps {
  provider: ProviderInfo;
  idx: number;
  dataSource: DataSource;
  roleOptions: RoleOption[];
  licenseTypes: string[];
  degreeTypes: string[];
  payTypes: SelectOption[];
  timesheetModes: SelectOption[];
  allProviderNames?: string[];
  showPayType?: boolean;
  /** Whether this card is expanded (editing mode). Managed by parent. */
  isExpanded?: boolean;
  /** Called when the card header is clicked to toggle expand/collapse. */
  onToggleExpand?: () => void;
  getRoleLabel: (role: string) => string;
  onRemove: (idx: number) => void;
  onUpdate: (idx: number, field: string, value: unknown) => void;
  onResolveConflict?: (idx: number, field: string, value: string) => void;
  onMergeWebsiteInfo?: (idx: number) => void;
  /** Save this individual provider (wizard: saves to DB, then collapses). */
  onSaveProvider?: () => void;
}

/* ─── Section components in display order ─── */
const SECTION_RENDERERS: Array<React.FC<SectionProps>> = [
  IdentitySection,
  ClinicalSection,
  BioSection,
  SchedulingSection,
  BillingSection,
  TelehealthSection,
  AddressSection,
];

export default function UnifiedProviderCard({
  provider,
  idx,
  dataSource,
  roleOptions,
  licenseTypes,
  degreeTypes,
  payTypes,
  timesheetModes,
  allProviderNames = [],
  showPayType = true,
  isExpanded: isExpandedProp = false,
  onToggleExpand,
  getRoleLabel,
  onRemove,
  onUpdate,
  onResolveConflict,
  onMergeWebsiteInfo,
  onSaveProvider,
}: UnifiedProviderCardProps) {
  const parentControlled = onToggleExpand !== undefined;
  const [localExpanded, setLocalExpanded] = useState(false);
  const isCardExpanded = parentControlled ? isExpandedProp : localExpanded;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const isActive = provider.is_active !== false;
  const displayName = [provider.first_name, provider.last_name].filter(Boolean).join(' ');

  const handlePhotoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const maxSize = 256;
      const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        onUpdate(idx, 'photo_url', canvas.toDataURL('image/jpeg', 0.8));
        onUpdate(idx, 'photo_url_external', '');
      }
      URL.revokeObjectURL(img.src);
    };
    const objectUrl = URL.createObjectURL(file);
    img.onerror = () => URL.revokeObjectURL(objectUrl);
    img.src = objectUrl;
  }, [idx, onUpdate]);

  const allRoles = useMemo(
    () => provider.roles?.length ? provider.roles : provider.role ? [provider.role] : [],
    [provider.roles, provider.role],
  );

  /* ─── Conflict banner ─── */
  const conflicts = provider._crawl_conflicts;
  const [editingConflict, setEditingConflict] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const handleKeepCurrent = useCallback((field: string) => {
    if (!onResolveConflict) return;
    const currentVal = (provider as unknown as Record<string, unknown>)[field];
    onResolveConflict(idx, field, String(currentVal ?? ''));
  }, [idx, provider, onResolveConflict]);

  const handleStartEdit = useCallback((field: string, initialValue: string) => {
    setEditingConflict(field);
    setEditDraft(initialValue);
  }, []);

  const handleSaveEdit = useCallback((field: string) => {
    if (!onResolveConflict) return;
    onResolveConflict(idx, field, editDraft);
    setEditingConflict(null);
  }, [idx, editDraft, onResolveConflict]);

  const conflictBanner = conflicts && Object.keys(conflicts).length > 0 && (
    <Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.300', borderRadius: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} />
          <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 600 }}>
            {Object.keys(conflicts).length} field{Object.keys(conflicts).length > 1 ? 's' : ''} differ from website
          </Typography>
        </Box>
        {onMergeWebsiteInfo && (
          <Chip
            label="Use all website values"
            size="small"
            color="info"
            variant="outlined"
            icon={<LanguageIcon sx={{ fontSize: 16 }} />}
            onClick={() => onMergeWebsiteInfo(idx)}
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>
      {Object.entries(conflicts).map(([field, conflict]) => {
        const c = conflict as CrawlConflict;
        const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
        const isEditing = editingConflict === field;
        return (
          <Box key={field} sx={{ mb: 1.5, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, textTransform: 'capitalize' }}>
              {fieldLabel}
            </Typography>
            {isEditing ? (
              <Box sx={{ mt: 0.5 }}>
                <TextField
                  fullWidth size="small" multiline minRows={2} maxRows={6}
                  value={editDraft} onChange={(e) => setEditDraft(e.target.value)}
                  placeholder="Enter your preferred value…"
                />
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  <Chip label="Save" size="small" color="primary" onClick={() => handleSaveEdit(field)} sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                  <Chip label="Cancel" size="small" variant="outlined" onClick={() => setEditingConflict(null)} sx={{ fontSize: '0.75rem' }} />
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <StorageIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Current
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', wordBreak: 'break-word', mb: 0.5 }}>
                    {String(c.db_value ?? '')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Chip label="Keep current" size="small" variant="outlined"
                      onClick={() => handleKeepCurrent(field)}
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                    <Chip label="Edit" size="small" variant="outlined" icon={<EditIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleStartEdit(field, String(c.db_value ?? ''))}
                      sx={{ fontSize: '0.75rem' }} />
                  </Box>
                </Box>
                <Box sx={{ p: 1, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <LanguageIcon sx={{ fontSize: 14, color: 'info.main' }} />
                    <Typography variant="caption" color="info.main" sx={{ fontWeight: 600 }}>
                      Website
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', wordBreak: 'break-word', mb: 0.5 }}>
                    {String(c.crawl_value ?? '')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {onResolveConflict && (
                      <Chip label="Use this" size="small" color="info"
                        onClick={() => onResolveConflict(idx, field, String(c.crawl_value))}
                        sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                    )}
                    <Chip label="Edit" size="small" variant="outlined" icon={<EditIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleStartEdit(field, String(c.crawl_value ?? ''))}
                      sx={{ fontSize: '0.75rem' }} />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );

  /* ─── Section props shared by all section renderers ─── */
  const sectionProps: SectionProps = {
    provider, idx, ds: dataSource, onUpdate,
    roleOptions, licenseTypes, degreeTypes, payTypes, timesheetModes,
    allProviderNames, showPayType, getRoleLabel,
  };

  /* ── helper: truncate long text for spreadsheet view ── */
  const truncate = (text: string | undefined | null, max = 60): string => {
    if (!text) return '—';
    return text.length > max ? `${text.slice(0, max)}…` : text;
  };

  /* ── Spreadsheet-style field cell ── */
  const fieldCell = (label: string, value: string | undefined | null, colSpan = 1) => (
    <Box sx={{ gridColumn: colSpan > 1 ? `span ${colSpan}` : undefined, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value || '—'}
      </Typography>
    </Box>
  );

  const handleHeaderClick = () => {
    if (parentControlled) {
      onToggleExpand?.();
    } else {
      setLocalExpanded(!localExpanded);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 0.5, opacity: isActive ? 1 : 0.55 }}>
      {/* ─── Clickable header: name row + controls ─── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1, py: 0.5,
          bgcolor: isCardExpanded ? 'primary.50' : isActive ? 'grey.50' : 'grey.100',
          borderBottom: isCardExpanded ? '2px solid' : 'none',
          borderColor: 'primary.main',
          cursor: 'pointer',
          '&:hover': { bgcolor: isCardExpanded ? 'primary.50' : 'action.hover' },
        }}
        onClick={handleHeaderClick}
      >
        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handlePhotoUpload} />
        <Avatar
          src={provider.photo_url || undefined}
          alt={displayName}
          sx={{ width: 28, height: 28, fontSize: '0.75rem', flexShrink: 0, cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
        >
          {(provider.first_name || '?').charAt(0)}{(provider.last_name || '').charAt(0)}
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {displayName || `Provider ${idx + 1}`}
        </Typography>
        {provider.title && (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {provider.title}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {conflicts && Object.keys(conflicts).length > 0 && (
          <Tooltip title={`${Object.keys(conflicts).length} field(s) differ from website`}>
            <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
          </Tooltip>
        )}
        {!isActive && <Chip label="Inactive" size="small" color="default" sx={{ height: 18, fontSize: '0.7rem' }} />}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <Switch size="small" checked={isActive} onChange={(e) => onUpdate(idx, 'is_active', e.target.checked)} />
          <IconButton size="small" onClick={() => onRemove(idx)} color="error" sx={{ p: 0.25 }} aria-label="Delete">
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <ExpandMoreIcon sx={{ fontSize: 18, color: 'action.active', transform: isCardExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
      </Box>

      {/* ─── Collapsed: spreadsheet-style data grid ─── */}
      {!isCardExpanded && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 0.5,
            px: 1.5, py: 0.75,
            bgcolor: 'background.paper',
            cursor: 'pointer',
          }}
          onClick={handleHeaderClick}
        >
          {/* Row 1: Role, Email, Phone, NPI */}
          {fieldCell('Role', allRoles.map(getRoleLabel).join(', '))}
          {fieldCell('Email', provider.email)}
          {fieldCell('Phone', provider.direct_phone)}
          {fieldCell('NPI', provider.npi_number)}
          {/* Row 2: Taxonomy, License, Degrees, Accepting */}
          {fieldCell('Taxonomy', provider.taxonomy_code)}
          {fieldCell('License', provider.license_type?.join(', '))}
          {fieldCell('Degrees', provider.degrees?.join(', '))}
          {fieldCell('Accepting', provider.accepting_new_clients === true ? 'Yes' : provider.accepting_new_clients === false ? 'No' : '—')}
          {/* Row 3: Bio (spans all 4 columns) */}
          {fieldCell('Bio', truncate(provider.bio, 120), 4)}
        </Box>
      )}

      {/* ─── Expanded: conflict banner + ALL sections flat (no accordions) ─── */}
      {isCardExpanded && (
        <Box sx={{ px: 1.5, py: 1 }}>
          {conflictBanner && (
            <Box sx={{ mb: 1 }}>{conflictBanner}</Box>
          )}
          {SECTION_RENDERERS.map((SectionComponent, num) => (
            <Box key={num} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.75, color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', pb: 0.25 }}>
                {SECTION_LABELS[num]}
              </Typography>
              <Grid container spacing={1}>
                <SectionComponent {...sectionProps} />
              </Grid>
            </Box>
          ))}

          {/* Save button at the bottom of expanded card */}
          {onSaveProvider && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={(e) => { e.stopPropagation(); onSaveProvider(); }}
              >
                Save {provider.first_name || 'Provider'}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Card>
  );
}
